import { memo, useState } from "react";
import { format, differenceInMinutes, isToday, isThisWeek } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Crown,
  Shield,
  Wrench,
  Clock,
  CheckCheck,
  ChevronDown,
  ChevronUp,
  Eye,
} from "lucide-react";
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { getInitials } from "@/utils/initials-format";
import { MensagemTipoAutor } from "@workflow/types";
import { MensagemRender } from "@/hooks/tarefas/use-tarefa-chat";
import { Person } from "@/components/shared/person";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { TarefaVisualizacaoDTO } from "@/types/tarefa.type";

// --- CONSTANTES E FUNÇÕES PURAS FORA DO COMPONENTE ---

const formatMessageTime = (dateInput: string | Date | undefined) => {
  if (!dateInput) return "";
  const date = new Date(dateInput);

  if (isToday(date)) return format(date, "HH:mm");

  if (isThisWeek(date, { weekStartsOn: 1 })) {
    const diaDaSemana = format(date, "EEEE, HH:mm", { locale: ptBR });
    return diaDaSemana.charAt(0).toUpperCase() + diaDaSemana.slice(1);
  }

  return format(date, "dd/MM/yyyy 'às' HH:mm");
};

const bubbleVariants = cva(
  "custom-scrollbar max-w-full overflow-x-auto whitespace-pre-wrap break-words border px-4 py-2.5 text-sm leading-relaxed shadow-sm transition-all",
  {
    variants: {
      variant: {
        minha:
          "rounded-2xl rounded-tr-none border-transparent bg-primary text-primary-foreground dark:text-foreground",
        criador:
          "rounded-2xl rounded-tl-none border-amber-200 bg-amber-50 text-foreground dark:border-amber-800/40 dark:bg-amber-950/20",
        default: "rounded-2xl rounded-tl-none border-border/40 bg-muted/40 text-foreground",
      },
      isOptimistic: {
        true: "opacity-70",
        false: "opacity-100",
      },
    },
    defaultVariants: {
      variant: "default",
      isOptimistic: false,
    },
  },
);

const RoleBadge = memo(({ role }: { role: MensagemTipoAutor }) => {
  if (role === "CRIADOR") {
    return (
      <span className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-tighter text-amber-600 dark:text-amber-400">
        <Crown className="h-3 w-3" /> Solicitante
      </span>
    );
  }

  if (role === "OBSERVADOR") {
    return (
      <span className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-tighter text-emerald-600 dark:text-emerald-400">
        <Eye className="h-3 w-3" /> Observador
      </span>
    );
  }

  if (role === "ADMIN") {
    return (
      <span className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-tighter text-red-600 dark:text-red-400">
        <Shield className="h-3 w-3" /> Admin
      </span>
    );
  }

  return (
    <span className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-tighter text-blue-600 dark:text-blue-400">
      <Wrench className="h-3 w-3" /> Responsável
    </span>
  );
});
RoleBadge.displayName = "RoleBadge";

interface TarefaChatMessageProps {
  msg: MensagemRender;
  isMinha: boolean;
  agruparComAnterior: boolean;
  isUltima: boolean;
  nextMsg: MensagemRender | null;
  /** Chegou depois da última vez que eu vi esta tarefa — destaque visual. */
  isNova?: boolean;
  /** Demais participantes (sem o autor) — só passado na última mensagem enviada por mim. */
  leitores?: TarefaVisualizacaoDTO[];
}

export const TarefaChatMessage = memo(
  ({
    msg,
    isMinha,
    agruparComAnterior,
    isUltima,
    nextMsg,
    isNova,
    leitores,
  }: TarefaChatMessageProps) => {
    // --- ESTADO DO LER MAIS ---
    const [isExpanded, setIsExpanded] = useState(false);
    const LIMITE_CARACTERES = 100; // Limite ideal para não ocupar mais que 1/3 da tela

    const hideAvatar =
      nextMsg &&
      nextMsg.criadoPor?.id === msg.criadoPor?.id &&
      differenceInMinutes(new Date(nextMsg.criadoEm as Date), new Date(msg.criadoEm as Date)) < 3;

    const bubbleVariant = isMinha ? "minha" : msg.tipo === "CRIADOR" ? "criador" : "default";

    // Lógica para determinar se a mensagem precisa ser cortada
    const textoMensagem = msg.mensagem || "";
    const isMensagemLonga = textoMensagem.length > LIMITE_CARACTERES;

    // Corta o texto se for longo e não estiver expandido.
    // O trimEnd() evita espaços sobrando antes do "..."
    const textoExibido =
      isMensagemLonga && !isExpanded
        ? textoMensagem.slice(0, LIMITE_CARACTERES).trimEnd() + "..."
        : textoMensagem;

    return (
      <div
        className={cn(
          "flex w-full gap-3 duration-300 animate-in fade-in slide-in-from-bottom-2",
          isMinha ? "justify-end" : "justify-start",
          agruparComAnterior ? "!mt-1" : "mt-6",
        )}
      >
        {!isMinha && (
          <div className="flex shrink-0 flex-col justify-end">
            {!hideAvatar && (
              <Person.Root pessoa={msg.criadoPor} asClickable>
                <Person.Avatar className="h-8 w-8 border border-border shadow-sm" />
              </Person.Root>
            )}
          </div>
        )}

        <div
          className={cn(
            "flex min-w-0 max-w-[85%] flex-col md:max-w-[75%]",
            isMinha ? "items-end" : "items-start",
          )}
        >
          {!agruparComAnterior && (
            <div
              className={cn(
                "mb-1 flex flex-wrap items-center gap-2 px-1",
                isMinha && "flex-row-reverse",
              )}
            >
              <span className="text-[11px] font-bold text-foreground/80">
                {isMinha ? (
                  "Você"
                ) : (
                  <Person.Root pessoa={msg.criadoPor} asClickable>
                    <Person.Name />
                  </Person.Root>
                )}
              </span>
              <RoleBadge role={msg.tipo} />
              {isNova && (
                <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-tighter text-primary">
                  Nova
                </span>
              )}
              <span className="whitespace-nowrap text-[10px] text-muted-foreground">
                {formatMessageTime(msg.criadoEm)}
              </span>
            </div>
          )}

          <div className="group relative flex min-w-0 max-w-full items-center gap-2">
            {agruparComAnterior && !isMinha && (
              <span className="pointer-events-none absolute -right-12 hidden select-none whitespace-nowrap text-[10px] text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 md:block">
                {msg.criadoEm ? format(new Date(msg.criadoEm), "HH:mm") : ""}
              </span>
            )}

            <div
              className={cn(
                bubbleVariants({ variant: bubbleVariant, isOptimistic: !!msg.isOptimistic }),
                isNova && "ring-2 ring-primary/50 ring-offset-1 ring-offset-background",
              )}
            >
              {/* O texto cortado ou inteiro */}
              <span>{textoExibido}</span>

              {/* Botão de Ler mais/menos injetado elegantemente no final do balão */}
              {isMensagemLonga && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="mt-2 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider opacity-80 transition-opacity hover:opacity-100 focus:outline-none"
                >
                  {isExpanded ? (
                    <>
                      Recolher texto <ChevronUp className="h-3 w-3" />
                    </>
                  ) : (
                    <>
                      Ler mais <ChevronDown className="h-3 w-3" />
                    </>
                  )}
                </button>
              )}
            </div>

            {agruparComAnterior && isMinha && (
              <span className="pointer-events-none absolute -left-12 hidden select-none whitespace-nowrap text-[10px] text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 md:block">
                {msg.criadoEm ? format(new Date(msg.criadoEm), "HH:mm") : ""}
              </span>
            )}
          </div>

          {isMinha && msg.isOptimistic && (
            <div className="mt-1 flex items-center gap-1 pr-1 text-[10px] text-muted-foreground">
              <Clock className="h-3 w-3 text-muted-foreground/60" /> Enviando
            </div>
          )}

          {isMinha && isUltima && !msg.isOptimistic && (
            <Tooltip>
              <TooltipTrigger className="mt-1 flex cursor-default items-center gap-1 pr-1 text-[10px] text-muted-foreground">
                Enviado <CheckCheck className="h-3 w-3 text-emerald-500" />
              </TooltipTrigger>
              <TooltipContent side="left" align="end" className="space-y-1">
                {!leitores || leitores.length === 0 ? (
                  <span>Ninguém mais na conversa</span>
                ) : (
                  leitores.map((leitor) => {
                    const viu =
                      !!leitor.visualizadoEm &&
                      !!msg.criadoEm &&
                      new Date(leitor.visualizadoEm) >= new Date(msg.criadoEm);

                    return (
                      <div
                        key={leitor.pessoa.id}
                        className="flex items-center gap-1.5 whitespace-nowrap"
                      >
                        {viu ? (
                          <CheckCheck className="h-3 w-3 shrink-0 text-emerald-500" />
                        ) : (
                          <Clock className="h-3 w-3 shrink-0 text-muted-foreground" />
                        )}
                        <span>
                          {leitor.pessoa.nome}
                          {viu && leitor.visualizadoEm
                            ? ` · ${format(new Date(leitor.visualizadoEm), "HH:mm")}`
                            : " · não visualizou"}
                        </span>
                      </div>
                    );
                  })
                )}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    );
  },
);
TarefaChatMessage.displayName = "TarefaChatMessage";
